import { z } from 'zod';
import { HexSchema } from '../hex/index.js';
import { ContentIdSchema } from './data/common.js';
import { LongNightWeekdaySchema } from './data/long-night.js';
import { HollowStageSchema, WalkKindSchema } from './hollow-stage.js';
import { SpeciesSchema } from './data/species.js';
import { ItemCountsSchema } from './inventory.js';
import { OwnedSquishySchema } from './squishies.js';
import { LocalDateSchema } from './time.js';

// The Hollow Man API (design doc §14; issue #21; tech spec §5). Nightfall is a
// server job; players see what it did (the morning report) and rescue their
// squishies from the Hollow. The night's seed, who was exposed on other
// players' land and the shadow guardians never appear here (CLAUDE.md rule 6).

/** One stop on the Hollow Man's walk (#277), which the client plays as a show. */
export const WalkPointSchema = z.object({
  q: HexSchema.shape.q,
  r: HexSchema.shape.r,
  kind: WalkKindSchema,
});
export type WalkPointView = z.infer<typeof WalkPointSchema>;

/** A squishy the Hollow Man took. */
export const TakenSquishySchema = z.object({
  squishyId: z.uuid(),
  speciesId: ContentIdSchema,
  nickname: z.string().nullable(),
  /** Still waiting in the Hollow (false once rescued). */
  inHollow: z.boolean(),
});
export type TakenSquishy = z.infer<typeof TakenSquishySchema>;

/** What one night did to me (the morning report, design doc §14). */
export const MorningReportSchema = z.object({
  /** The night, as the map-local date its nightfall fell on. */
  night: LocalDateSchema,
  /** The squishies the Hollow Man took to the Hollow (#277: up to 3); empty if everyone stayed safe. */
  taken: z.array(TakenSquishySchema),
  /** My squishies kept safe that night: by a lit fire, or standing watch. */
  sheltered: z.number().int().min(0),
  /**
   * My squishies left in the dark that night, the taken ones included. More
   * than none with nobody taken means he let them be (first-night grace):
   * the report says so and nudges for a fire, so the quiet nights still teach.
   */
  exposed: z.number().int().min(0),
  /** My dark land he won back that night: it went wild (#277). */
  reclaimed: z.array(HexSchema),
  /** How bold he was with me that night. */
  stage: HollowStageSchema,
  /** His walk along my border, for the replay (empty for nights before #277). */
  walk: z.array(WalkPointSchema),
  /** What stood on the land he won back; it came down and gave back its take-down share. */
  lostBuildings: z.object({
    fires: z.number().int().min(0),
    fences: z.number().int().min(0),
    trainingGrounds: z.number().int().min(0),
    /** Resource buildings (#601); older reports have none. */
    resourceBuildings: z.number().int().min(0).optional(),
    /** The Warehouse (#601); older reports have none. */
    warehouses: z.number().int().min(0).optional(),
  }),
});
export type MorningReport = z.infer<typeof MorningReportSchema>;

/** `GET /maps/:mapId/hollow`: the night, my morning reports and my squishies in the Hollow. */
export const HollowStatusSchema = z.object({
  night: z.object({
    /** Between nightfall and morning, map time: the map is drawn at night. */
    isNight: z.boolean(),
    /** Ask again after this many minutes: night falls or morning comes. */
    changesInMinutes: z.number().int().min(1),
  }),
  /**
   * The coming (or current) night for me (#277): how bold he'll be, and
   * when the show's strike lands (nightfall plus `HOLLOW_RULES.show`). The
   * client holds back what he took until then, or until the kid skips.
   */
  tonight: z.object({
    night: LocalDateSchema,
    stage: HollowStageSchema,
    nightfallAt: z.iso.datetime(),
    strikeAt: z.iso.datetime(),
  }),
  /** Nights the Hollow Man has come by lately, newest first. */
  reports: z.array(MorningReportSchema),
  /** My squishies in the Hollow, waiting to be rescued. */
  hollowed: z.array(OwnedSquishySchema),
  /** Rows the public species table doesn't have, for my own squishies (a secret one I befriended). */
  speciesDefs: z.array(SpeciesSchema),
  rescue: z.object({
    /** Heartdust a rewarded rescue earns. */
    heartdust: z.number().int().min(1),
    /** Rescues left today that earn it (decision C); rescues past it still bring them home. */
    rewardsLeftToday: z.number().int().min(0),
  }),
  /**
   * A cozy nudge to light a fire: the Hollow Man's first visit to me (after
   * my first-night grace) is tonight or still to come, and one of my
   * squishies would spend tonight in the dark (a gatherer or a guard out on
   * land no lit fire reaches, or a trainee on a dark homestead, #277; home is
   * always safe, owner decisions 2026-10-07).
   */
  fireHint: z.boolean(),
  /**
   * My home fire packed up when the Heart Seed began keeping home safe
   * (#202, owner decision 2026-10-07): what came back, and when. The
   * morning report says so once per device; null when I never had one.
   */
  homeFirePacked: z.object({ refund: ItemCountsSchema, at: z.iso.datetime() }).nullable(),
  /**
   * My home Training Grounds packed up when they moved to homesteads (#277,
   * owner decision 4): what came back (all of it), and when. Said once per
   * device, like `homeFirePacked`; null when I never had one at home.
   */
  trainingGroundsPacked: z.object({ refund: ItemCountsSchema, at: z.iso.datetime() }).nullable(),
  /**
   * One building per tile (#295): what the one-time tidy-up did to my
   * buildings: which moved where, which had no free tile and came down
   * (everything they cost back, in `refund`), and which squishies stopped
   * gathering on a tile that keeps a building (what they had ready banked).
   * Said once per device, like `homeFirePacked`. Null, or missing from an
   * older server: nothing changed.
   */
  tileUseNote: z
    .object({
      moved: z.array(z.object({ buildingId: ContentIdSchema, from: HexSchema, to: HexSchema })),
      packed: z.array(
        z.object({ buildingId: ContentIdSchema, q: HexSchema.shape.q, r: HexSchema.shape.r }),
      ),
      refund: ItemCountsSchema,
      stoppedGatherers: z.array(z.object({ squishyId: z.uuid(), name: z.string() })),
      at: z.iso.datetime(),
    })
    .nullable()
    .optional(),
  /**
   * Tiles still crowded with 2+ buildings when one building per tile went
   * into the database (#295, owner decision 2026-10-18): every building on
   * them came down, and this is everything that came back. Said once per
   * device, like `homeFirePacked`. Null, or missing from an older server:
   * nothing came down.
   */
  crowdedTilesPacked: z
    .object({ refund: ItemCountsSchema, at: z.iso.datetime() })
    .nullable()
    .optional(),
  /**
   * Trick-or-treaters at my home yesterday (#515, the patch's day): how many
   * came and the thank-you Heartdust they left. Said once per device per day
   * in the morning report. Null, or missing from an older server: nobody came.
   */
  trickOrTreat: z
    .object({
      day: LocalDateSchema,
      visitors: z.number().int().min(1),
      heartdust: z.number().int().min(0),
    })
    .nullable()
    .optional(),
  /**
   * Wild Isle (#621): my buildings storms knocked about over the last few
   * nights and still waiting to be fixed, newest first, for the morning
   * card ("The storm knocked your pier about 🌧️"). Left out on a Cozy
   * Patch and by an older server.
   */
  weatherNotes: z
    .array(
      z.object({
        buildingRowId: z.uuid(),
        buildingId: ContentIdSchema,
        q: HexSchema.shape.q,
        r: HexSchema.shape.r,
        at: z.iso.datetime(),
      }),
    )
    .optional(),
  /**
   * The Long Night (#727): the patch's weekday, when the next one falls and
   * Juniper's Gap after a patch held. Worked out on read (rule 4). Left out
   * while the Long Night is switched off for me (`HP_LONG_NIGHT`) and by
   * older servers. How much stronger he is never comes (rule 6).
   */
  longNight: z
    .object({
      /** The patch's weekday (0 is Sunday), or null: Off on this patch. */
      weekday: LongNightWeekdaySchema.nullable(),
      /** The coming (or current) night, `tonight.night`, is a Long Night. */
      tonight: z.boolean(),
      /** The next Long Night's nightfall, or null while it's Off. */
      nextAt: z.iso.datetime().nullable(),
      /**
       * Juniper's Gap, from the patch's latest Long Night that held, while
       * it's open (`opensAt <= now < closesAt`); null otherwise. `available`
       * says I still have an encounter to claim there; which squishy waits
       * stays on the server until the battle starts (rule 6).
       */
      gap: z
        .object({
          night: LocalDateSchema,
          opensAt: z.iso.datetime(),
          closesAt: z.iso.datetime(),
          available: z.boolean(),
        })
        .nullable(),
      /**
       * The patch's latest Long Night's result (727e2), from its stored row,
       * so a Keeper who wasn't online for it still hears how it went: the
       * reveal and the morning block. It's on the row from nightfall, before
       * the strike lands; the client waits for 7:30 to tell it. Left out
       * before the patch's first Long Night (and by older servers).
       * `heldBy` is the Keepers he took nothing from, in member order;
       * `keeperOfNight` the crown, or null.
       */
      last: z
        .object({
          night: LocalDateSchema,
          held: z.boolean(),
          heldBy: z.array(z.uuid()),
          keeperOfNight: z.uuid().nullable(),
        })
        .optional(),
    })
    .optional(),
  /** The server's clock. */
  now: z.iso.datetime(),
});
export type HollowStatus = z.infer<typeof HollowStatusSchema>;

export const HollowResponseSchema = z.object({ hollow: HollowStatusSchema });
export type HollowResponse = z.infer<typeof HollowResponseSchema>;

/**
 * `POST /maps/:mapId/rescues`: set off to rescue one of my squishies from the
 * Hollow, from anywhere on the map (decision C). Answers with the rescue
 * battle (or the battle already going).
 */
export const StartRescueRequestSchema = z.strictObject({ squishyId: z.uuid() });
export type StartRescueRequest = z.infer<typeof StartRescueRequestSchema>;

/**
 * `POST /maps/:mapId/dev/nightfall` (dev/test only): night falls on the map
 * now, as the next night that hasn't come yet.
 */
export const DevNightfallResponseSchema = z.object({
  night: LocalDateSchema,
  /** How many squishies were taken. */
  taken: z.number().int().min(0),
});
export type DevNightfallResponse = z.infer<typeof DevNightfallResponseSchema>;

/**
 * `POST /maps/:mapId/dev/gap-now` (dev/test only, 727e2): the patch's latest
 * held Long Night opens Juniper's Gap from now, for as long as a Gap stays
 * open, so an e2e can go look by day. The Night's result is untouched.
 */
export const DevGapNowResponseSchema = z.object({
  night: LocalDateSchema,
  opensAt: z.iso.datetime(),
  closesAt: z.iso.datetime(),
});
export type DevGapNowResponse = z.infer<typeof DevGapNowResponseSchema>;
